import { apiClient } from "./api";

// Campaign types
export interface Question {
  id?: string;
  text: string;
  type: "TEXT" | "EMOJI" | "MULTIPLE" | "RATING";
  options?: string[];
  order: number;
  ratingScale?: number;
  ratingLabels?: string;
  customLabels?: string[];
}

export interface PricingOption {
  id?: string;
  price: string;
  description: string;
  order: number;
}

export interface CreateCampaignRequest {
  title: string;
  tagline: string;
  description: string;
  problemStatement?: string;
  solution?: string;
  category?: string;
  bannerImage?: string;
  isPrivate?: boolean;
  status?: "DRAFT" | "ACTIVE" | "PAUSED" | "ARCHIVED";
  questions?: Question[];
  pricingOptions?: PricingOption[];
}

export interface UpdateCampaignRequest {
  title?: string;
  tagline?: string;
  description?: string;
  problemStatement?: string;
  solution?: string;
  category?: string;
  bannerImage?: string;
  isPrivate?: boolean;
  status?: "DRAFT" | "ACTIVE" | "PAUSED" | "SUSPENDED" | "ARCHIVED";
  questions?: Question[];
  pricingOptions?: PricingOption[];
}

export interface Campaign {
  id: string;
  title: string;
  tagline: string;
  description: string;
  problemStatement?: string;
  solution?: string;
  category?: string;
  bannerImage?: string;
  isPrivate: boolean;
  status: "DRAFT" | "ACTIVE" | "PAUSED" | "SUSPENDED" | "ARCHIVED";
  createdAt: string;
  updatedAt: string;
  creatorId: string;
  creator: {
    id: string;
    name: string;
    email: string;
  };
  questions: Question[];
  pricingOptions: PricingOption[];
  _count: {
    responses: number;
    comments: number;
  };
}

export interface CampaignListParams {
  isPrivate?: boolean;
}

export interface Asset {
  id: string;
  type: "IMAGE" | "VIDEO" | "LINK" | "FILE";
  url: string;
  title: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  campaignId: string;
}

export interface CreateAssetRequest {
  type: "IMAGE" | "VIDEO" | "LINK" | "FILE";
  url: string;
  title: string;
  description?: string;
}

// Comment types
export interface Comment {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  campaignId: string;
  parentId?: string;
  user: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  replies: Comment[];
  _count: {
    likes: number;
  };
  isLiked?: boolean;
}

export interface CreateCommentRequest {
  content: string;
  parentId?: string;
}

export interface UpdateCommentRequest {
  content?: string;
}

export interface BloomScoreResponse {
  action: "liked" | "unliked";
  bloomScore: number;
}

// Waitlist types
export interface WaitlistSubscription {
  id: string;
  email: string;
  isActive: boolean;
  subscribedAt: string;
  updatedAt: string;
  campaignId: string;
}

export interface CreateWaitlistSubscriptionRequest {
  email: string;
}

export interface WaitlistCountResponse {
  count: number;
}

// Pricing feedback types
export interface PricingFeedback {
  id: string;
  sentiment: "LOVE" | "LIKE" | "NEUTRAL" | "EXPENSIVE" | "NOT_INTERESTED";
  willingnessToPay?: number;
  comment?: string;
  createdAt: string;
  userId?: string;
  user?: {
    id: string;
    name: string;
  };
  selectedPricingOptionId?: string;
  selectedPricingOption?: {
    id: string;
    price: string;
    description: string;
  };
}

export interface CreatePricingFeedbackRequest {
  sentiment: "LOVE" | "LIKE" | "NEUTRAL" | "EXPENSIVE" | "NOT_INTERESTED";
  willingnessToPay?: number;
  comment?: string;
  selectedPricingOptionId?: string;
}

export interface PricingFeedbackAnalytics {
  totalFeedback: number;
  sentimentDistribution: Array<{
    sentiment: string;
    _count: { sentiment: number };
  }>;
  willingnessToPayStats: {
    _avg: { willingnessToPay: number | null };
    _min: { willingnessToPay: number | null };
    _max: { willingnessToPay: number | null };
    _count: { willingnessToPay: number };
  };
  pricingOptionPreferences: Array<{
    selectedPricingOptionId: string | null;
    _count: { selectedPricingOptionId: number };
  }>;
}

// Response types
export interface Response {
  id: string;
  answer: string;
  createdAt: string;
  questionId: string;
  userId: string | null;
  campaignId: string;
  question: Question;
  user: {
    id: string;
    name: string;
    avatarUrl?: string;
  } | null;
}

export interface CreateResponseRequest {
  questionId: string;
  answer: string;
}

// Campaigns API service
export const campaignsApi = {
  // Get all campaigns
  async getCampaigns(params?: CampaignListParams): Promise<Campaign[]> {
    const queryParams = new URLSearchParams();
    if (params?.isPrivate !== undefined) {
      queryParams.append("isPrivate", params.isPrivate.toString());
    }

    const endpoint = queryParams.toString()
      ? `/campaigns?${queryParams}`
      : "/campaigns";
    return apiClient.get<Campaign[]>(endpoint);
  },

  // Get user's campaigns
  async getMyCampaigns(): Promise<Campaign[]> {
    return apiClient.get<Campaign[]>("/campaigns/my");
  },

  // Get single campaign
  async getCampaign(id: string): Promise<Campaign> {
    return apiClient.get<Campaign>(`/campaigns/${id}`);
  },

  // Create campaign
  async createCampaign(data: CreateCampaignRequest): Promise<Campaign> {
    return apiClient.post<Campaign>("/campaigns", data);
  },

  // Update campaign
  async updateCampaign(
    id: string,
    data: UpdateCampaignRequest
  ): Promise<Campaign> {
    return apiClient.patch<Campaign>(`/campaigns/${id}`, data);
  },

  // Delete campaign
  async deleteCampaign(id: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/campaigns/${id}`);
  },

  // Get campaign assets
  async getCampaignAssets(campaignId: string): Promise<Asset[]> {
    return apiClient.get<Asset[]>(`/campaigns/${campaignId}/assets`);
  },

  // Create asset (for links, images, etc. with URL)
  async createAsset(
    campaignId: string,
    data: CreateAssetRequest
  ): Promise<Asset> {
    return apiClient.post<Asset>(`/campaigns/${campaignId}/assets`, data);
  },

  // Upload asset file (for files/images/videos)
  async uploadAssetFile(
    campaignId: string,
    file: File,
    title: string,
    description: string
  ): Promise<Asset> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);
    formData.append("description", description);
    return apiClient.postForm<Asset>(
      `/campaigns/${campaignId}/assets/upload`,
      formData
    );
  },

  // Delete asset
  async deleteAsset(assetId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `/campaigns/assets/${assetId}`
    );
  },

  // Comment management methods
  async getCampaignComments(campaignId: string): Promise<Comment[]> {
    return apiClient.get<Comment[]>(`/campaigns/${campaignId}/comments`);
  },

  async createComment(
    campaignId: string,
    data: CreateCommentRequest
  ): Promise<Comment> {
    return apiClient.post<Comment>(`/campaigns/${campaignId}/comments`, data);
  },

  async updateComment(
    commentId: string,
    data: UpdateCommentRequest
  ): Promise<Comment> {
    return apiClient.patch<Comment>(`/campaigns/comments/${commentId}`, data);
  },

  async deleteComment(commentId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `/campaigns/comments/${commentId}`
    );
  },

  // Bloom Score methods
  async toggleBloomScore(commentId: string): Promise<BloomScoreResponse> {
    return apiClient.post<BloomScoreResponse>(
      `/campaigns/comments/${commentId}/bloom-score`
    );
  },

  async getCommentBloomScore(
    commentId: string
  ): Promise<{ bloomScore: number }> {
    return apiClient.get<{ bloomScore: number }>(
      `/campaigns/comments/${commentId}/bloom-score`
    );
  },

  // Waitlist methods
  async subscribeToWaitlist(
    campaignId: string,
    data: CreateWaitlistSubscriptionRequest
  ): Promise<WaitlistSubscription> {
    return apiClient.post<WaitlistSubscription>(
      `/campaigns/${campaignId}/waitlist`,
      data
    );
  },

  async getWaitlistSubscriptions(
    campaignId: string
  ): Promise<WaitlistSubscription[]> {
    return apiClient.get<WaitlistSubscription[]>(
      `/campaigns/${campaignId}/waitlist`
    );
  },

  async unsubscribeFromWaitlist(
    campaignId: string,
    email: string
  ): Promise<WaitlistSubscription> {
    return apiClient.delete<WaitlistSubscription>(
      `/campaigns/${campaignId}/waitlist/${encodeURIComponent(email)}`
    );
  },

  async getWaitlistCount(campaignId: string): Promise<WaitlistCountResponse> {
    return apiClient.get<WaitlistCountResponse>(
      `/campaigns/${campaignId}/waitlist/count`
    );
  },

  // Pricing feedback functions
  async createPricingFeedback(
    campaignId: string,
    data: CreatePricingFeedbackRequest
  ): Promise<PricingFeedback> {
    return apiClient.post<PricingFeedback>(
      `/campaigns/${campaignId}/pricing-feedback`,
      data
    );
  },

  async getPricingFeedback(campaignId: string): Promise<PricingFeedback[]> {
    return apiClient.get<PricingFeedback[]>(
      `/campaigns/${campaignId}/pricing-feedback`
    );
  },

  async getPricingFeedbackAnalytics(
    campaignId: string
  ): Promise<PricingFeedbackAnalytics> {
    return apiClient.get<PricingFeedbackAnalytics>(
      `/campaigns/${campaignId}/pricing-feedback/analytics`
    );
  },

  // Response functions
  async createResponse(
    campaignId: string,
    data: CreateResponseRequest
  ): Promise<Response> {
    return apiClient.post<Response>(`/campaigns/${campaignId}/responses`, data);
  },

  async createMultipleResponses(
    campaignId: string,
    responses: CreateResponseRequest[]
  ): Promise<Response[]> {
    return apiClient.post<Response[]>(
      `/campaigns/${campaignId}/responses/batch`,
      responses
    );
  },

  async getCampaignResponses(campaignId: string): Promise<Response[]> {
    return apiClient.get<Response[]>(`/campaigns/${campaignId}/responses`);
  },

  async getUserResponsesForCampaign(campaignId: string): Promise<Response[]> {
    return apiClient.get<Response[]>(`/campaigns/${campaignId}/responses/user`);
  },

  async hasUserRespondedToCampaign(campaignId: string): Promise<boolean> {
    return apiClient.get<boolean>(`/campaigns/${campaignId}/responses/check`);
  },

  // Campaign Moderation API
  async moderateCampaign(
    campaignId: string,
    action: "SUSPEND" | "REACTIVATE" | "DELETE",
    reason?: string
  ): Promise<Campaign> {
    return apiClient.post<Campaign>(`/campaigns/${campaignId}/moderate`, {
      action,
      reason,
    });
  },

  async bulkModerateCampaigns(
    campaignIds: string[],
    action: "SUSPEND" | "REACTIVATE" | "DELETE",
    reason?: string
  ): Promise<{
    action: string;
    totalCampaigns: number;
    results: Array<{
      campaignId: string;
      success: boolean;
      result?: Campaign;
      error?: string;
    }>;
  }> {
    return apiClient.post(`/campaigns/bulk-moderate`, {
      campaignIds,
      action,
      reason,
    });
  },

  async getCampaignsForModeration(
    page: number = 1,
    limit: number = 20,
    search?: string,
    status?: string
  ): Promise<{
    campaigns: Campaign[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (search) params.append("search", search);
    if (status && status !== "all") params.append("status", status);

    return apiClient.get(`/campaigns/admin/moderation?${params.toString()}`);
  },
};
